import React from "react";
import { motion } from "framer-motion";
import { AiOutlineClose } from "react-icons/ai";
import ReactLoading from "react-loading";

export enum UserActionType {
  Suspended = "SUSPENDED",
  Activate = "ACTIVE",
  Blocked = "BLOCKED",
  Deleted = "DELETED",
}

interface UserActionModalProps {
  isOpen: boolean;
  actionType: UserActionType;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  userName?: string;
}

const UserActionModal: React.FC<UserActionModalProps> = ({
  isOpen,
  actionType,
  onClose,
  onConfirm,
  isLoading = false,
  userName = "User",
}) => {
  const modalRef = React.useRef(null);

  const handleOverlayClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  // Define UI configurations based on action type
  const getActionConfig = () => {
    switch (actionType) {
      case UserActionType.Suspended:
        return {
          title: "Suspend User",
          message: `Are you sure you want to suspend ${userName}?`,
          description: "All access would be suspended with immediate effect",
          buttonColor: "bg-red-500 hover:bg-red-600",
          buttonText: "Suspend",
          iconColor: "text-red-500",
          closeButtonColor: "hover:bg-red-100",
        };
      case UserActionType.Activate:
        return {
          title: "Reactivate User",
          message: `Are you sure you want to reactivate ${userName}?`,
          description: "All access would be granted with immediate effect",
          buttonColor: "bg-green-500 hover:bg-green-600",
          buttonText: "Activate",
          iconColor: "text-green-500",
          closeButtonColor: "hover:bg-green-100",
        };
      case UserActionType.Blocked:
        return {
          title: "Block User",
          message: `Are you sure you want to block ${userName}?`,
          description: "User will be blocked from using the platform",
          buttonColor: "bg-orange-500 hover:bg-orange-600",
          buttonText: "Block",
          iconColor: "text-orange-500",
          closeButtonColor: "hover:bg-orange-100",
        };
      case UserActionType.Deleted:
        return {
          title: "Delete User",
          message: `Are you sure you want to delete ${userName}?`,
          description: "This action cannot be undone",
          buttonColor: "bg-red-600 hover:bg-red-700",
          buttonText: "Delete",
          iconColor: "text-red-600",
          closeButtonColor: "hover:bg-red-100",
        };
      default:
        return {
          title: "Confirm Action",
          message: "Are you sure you want to perform this action?",
          description: "Please confirm to proceed",
          buttonColor: "bg-blue-500 hover:bg-blue-600",
          buttonText: "Confirm",
          iconColor: "text-blue-500",
          closeButtonColor: "hover:bg-blue-100",
        };
    }
  };

  const config = getActionConfig();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-white rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{config.title}</h2>
          <button
            onClick={onClose}
            className={`text-gray-500 ${config.closeButtonColor} rounded-full p-2`}
          >
            <AiOutlineClose size={20} />
          </button>
        </div>

        <p className="text-gray-600 mb-2">{config.message}</p>
        <p className="text-gray-500 text-sm mb-6">{config.description}</p>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 rounded-md text-white ${config.buttonColor} ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? (
              <div className="flex justify-center">
                <ReactLoading
                  type="spinningBubbles"
                  color="#fff"
                  height={24}
                  width={24}
                />
              </div>
            ) : (
              config.buttonText
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserActionModal;
